import React from 'react';
import PropTypes from 'prop-types';
import {
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { DRNModal } from '@traveloka/district-ui';

import {
  pageState,
  withPageStateProvider,
} from 'common/libs/PageState/pageState';

import R from 'common/res/R';
import BusTransactionListItemContainer from '../containers/BusTransactionListItemContainer';
import { BOOKING_SEARCH_RESULT_STATUS_EMPTY_STATE } from 'modules/bus/booking/search-result/constants/busBookingFetchSearchResultContants';
import UnavailableRouteException from 'common/exceptions/UnavailableRouteException';
import i18n from 'common/locale/i18n';
import PopupModal from 'common/components/PopupModal/PopupModal';
import TemporarySystemDisruptionException from 'common/exceptions/TemporarySystemDisruptionException';
import NavigationService from 'common/libs/react-navigation/NavigationService';
import AuthorizeView from 'common/components/AuthorizeView/AuthorizeView';
import {
  ISSUE_RESERVATION_PERMISSION,
  CANCEL_RESERVATION_PERMISSION,
} from 'common/constants/authPermissionConstants';
import withAuth from 'common/hoc/withAuth';
import permissionRequiredAny from 'common/utils/validation/permissionRequiredAny';
import BusTransactionListFilter from './BusTransactionListFilter';
import BusWagonTab from 'modules/bus/booking/seat-selection/components/BusWagonTab/BusWagonTab';

const styles = StyleSheet.create({
  contentContainer: {
    backgroundColor: R.color.background,
    padding: R.spacing.m,
    display: 'flex',
    flexDirection: 'column',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    margin: 0,
  },
  modalContent: {
    backgroundColor: R.color.uiLightPrimary,
    flexDirection: 'column',
    justifyContent: 'flex-end',
    margin: 0,
  },
  modalActionButton: {
    paddingVertical: R.spacing.s,
    paddingHorizontal: R.spacing.xxs,
  },
  modalActionButtonText: {
    marginLeft: R.spacing.s,
  },
});

@withAuth
@withPageStateProvider()
@pageState()
class BusTransactionList extends React.Component {
  static propTypes = {
    cancelBooking: PropTypes.func.isRequired,
    fetchTransactionList: PropTypes.func.isRequired,
    issueBooking: PropTypes.func.isRequired,
    inventories: PropTypes.array.isRequired,
    navigation: PropTypes.shape().isRequired,
  };

  constructor(props) {
    super(props);

    this.flatList = React.createRef();
  }

  state = {
    isRefreshing: false,
    isReservationList: false,
    selectedTransaction: null,
    isIssueModalVisible: false,
    isCancelModalVisible: false,
  };

  latestScrolledPage = 1;

  get filter() {
    const { navigation, startDate, endDate, agentId } = this.props;
    const phoneNumber = navigation.getParam('phoneNumber', null);
    return {
      isReservationList: navigation.getParam('isReservationList', false),
      name: navigation.getParam('name', null),
      phoneNumber: phoneNumber
        ? {
            phoneNumber,
            countryCode: '+62',
          }
        : null,
      startDate,
      endDate,
      agentId,
    };
  }

  getNonNullFilter() {
    const filter = {
      isReservationList: this.filter.isReservationList,
    };
    if (this.filter.phoneNumber) {
      filter.phoneNumber = this.filter.phoneNumber;
    }
    if (this.filter.name) {
      filter.name = this.filter.name;
    }
    if (this.filter.startDate) {
      filter.startDate = this.filter.startDate;
    }
    if (this.filter.endDate) {
      filter.endDate = this.filter.endDate;
    }
    if (this.filter.agentId) {
      filter.agentId = this.filter.agentId;
    }
    return filter;
  }

  componentDidMount() {
    this.fetchTransactionList();
  }

  setIssueModalVisible = value => {
    this.setState({
      isIssueModalVisible: value,
    });
  };

  setCancelModalVisible = value => {
    this.setState({
      isCancelModalVisible: value,
    });
  };

  @pageState({ error: 'page.error', loading: 'page.loading ' })
  async fetchTransactionList() {
    this.latestScrolledPage = 1;
    const { error } = await this.props.fetchTransactionList({
      page: 1,
      filter: this.getNonNullFilter(),
    });

    if (error === BOOKING_SEARCH_RESULT_STATUS_EMPTY_STATE) {
      throw new UnavailableRouteException(
        i18n.getText('error_no_reservation_message'),
        {
          title: i18n.getText('error_no_reservation_title'),
          ctaButtonLabel: 'Kembali ke pencarian',
          onClickCtaButton: this.handleBackToSearchForm,
        }
      );
    }
  }

  handleRefresh = async e => {
    this.setState({ isRefreshing: true });
    await this.fetchTransactionList();
    this.setState({ isRefreshing: false });
  };

  @pageState({ error: 'page.error' })
  handleLoadMore = async e => {
    this.setState({ isRefreshing: true });
    if (!this.props.disableFetch) {
      this.latestScrolledPage += 1;
      await this.props.fetchTransactionList({
        page: this.latestScrolledPage,
        filter: this.getNonNullFilter(),
        shouldAppendToCurrentState: true,
      });
    }
    this.setState({ isRefreshing: false });
  };

  handleOnModalRequestClose = () => {
    this.setState({ selectedTransaction: null });
  };

  handleOnTransactionItemActionButtonPressed = selectedTransaction => {
    this.setState({ selectedTransaction });
  };

  @pageState({ loading: 'page.loading', error: 'page.error' })
  issueBooking = async () => {
    const { result } = await this.props.issueBooking(
      this.state.selectedTransaction
    );

    if (result.data.status === 'OK') {
      this.setIssueModalVisible(false);
      this.handleOnModalRequestClose();
      this.handleRefresh();
      return;
    }

    throw new TemporarySystemDisruptionException(result.data.message, {
      ctaButtonLabel: 'Kembali ke pencarian',
      onClickCtaButton: () => {
        NavigationService.navigateFromRoot('BusReservationList');
      },
    });
  };

  @pageState({ loading: 'page.loading', error: 'page.error ' })
  cancelBooking = async () => {
    const { result } = await this.props.cancelBooking(
      this.state.selectedTransaction
    );

    if (result.data.status === 'OK') {
      this.setCancelModalVisible(false);
      this.handleOnModalRequestClose();
      this.handleRefresh();
      return;
    }

    throw new TemporarySystemDisruptionException(result.data.message, {
      ctaButtonLabel: 'Kembali ke pencarian',
      onClickCtaButton: () => {
        NavigationService.navigateFromRoot('BusReservationList');
      },
    });
  };

  handleApplyFilter = async (agentId, startDateMDY, endDateMDY) => {
    this.props.inventories.length > 0 &&
      (await this.flatList.current.scrollToIndex({
        index: 0,
        animated: false,
      }));
    this.latestScrolledPage = 1;
    const { error } = await this.props.fetchTransactionList({
      page: 1,
      filter: {
        ...this.getNonNullFilter(),
        agentId,
        startDate: startDateMDY,
        endDate: endDateMDY,
      },
    });

    if (error === BOOKING_SEARCH_RESULT_STATUS_EMPTY_STATE) {
      throw new UnavailableRouteException(
        i18n.getText('error_no_reservation_message'),
        {
          title: i18n.getText('error_no_reservation_title'),
          ctaButtonLabel: 'Kembali ke pencarian',
          onClickCtaButton: this.handleBackToSearchForm,
        }
      );
    }
  };

  handleBackToSearchForm = () => {
    this.props.navigation.pop();
  };

  handleResetFilters = () => {
    this.props.resetFilters();
  };

  renderTab = (activeIndex, handleChangeTab) => {
    return (
      <BusWagonTab
        tabs={['Laporan', 'Daftar Transaksi']}
        activeTab={activeIndex}
        onClickTab={handleChangeTab}
      />
    );
  };

  render() {
    const { userPermission } = this.props;
    const { isCancelModalVisible, isIssueModalVisible } = this.state;

    return (
      <React.Fragment>
        {!this.filter.isReservationList && (
          <BusTransactionListFilter
            onApplyFilter={this.handleApplyFilter}
            onResetFilters={this.handleResetFilters}
          />
        )}

        <FlatList
          ref={this.flatList}
          contentContainerStyle={styles.contentContainer}
          data={this.props.inventories}
          keyExtractor={id => id}
          renderItem={({ item: id, index }) => (
            <BusTransactionListItemContainer
              id={id}
              index={index}
              isReservationList={this.filter.isReservationList}
              onActionButtonPressed={
                permissionRequiredAny({
                  requiredPermission: [
                    ISSUE_RESERVATION_PERMISSION,
                    CANCEL_RESERVATION_PERMISSION,
                  ],
                  userPermission,
                })
                  ? this.handleOnTransactionItemActionButtonPressed
                  : undefined
              }
            />
          )}
          refreshing={this.state.isRefreshing}
          onRefresh={this.handleRefresh}
          onEndReached={this.handleLoadMore}
          onEndThreshold={0}
        />
        {this.filter.isReservationList && (
          <DRNModal
            isVisible={Boolean(this.state.selectedTransaction)}
            onBackButtonPress={this.handleOnModalRequestClose}
            onBackdropPress={this.handleOnModalRequestClose}
            style={styles.modalContainer}
          >
            <View style={styles.modalContent}>
              <AuthorizeView.RequiredAll
                requiredPermission={[ISSUE_RESERVATION_PERMISSION]}
              >
                <TouchableOpacity
                  style={styles.modalActionButton}
                  onPress={() => this.setIssueModalVisible(true)}
                >
                  <Text style={styles.modalActionButtonText}>
                    Bayar Pesanan
                  </Text>
                </TouchableOpacity>
              </AuthorizeView.RequiredAll>

              <AuthorizeView.RequiredAll
                requiredPermission={[CANCEL_RESERVATION_PERMISSION]}
              >
                <TouchableOpacity
                  style={styles.modalActionButton}
                  onPress={() => this.setCancelModalVisible(true)}
                >
                  <Text style={styles.modalActionButtonText}>
                    Batalkan Pesanan
                  </Text>
                </TouchableOpacity>
              </AuthorizeView.RequiredAll>

              <TouchableOpacity
                style={styles.modalActionButton}
                onPress={this.handleOnModalRequestClose}
              >
                <Text style={styles.modalActionButtonText}>Tutup</Text>
              </TouchableOpacity>

              <PopupModal
                isVisible={isIssueModalVisible}
                onBackdropPress={() => this.setIssueModalVisible(false)}
              >
                <PopupModal.Header>Cetak Tiket</PopupModal.Header>
                <PopupModal.Content>
                  Apakah Anda yakin ingin mencetak tiket?
                </PopupModal.Content>
                <PopupModal.ButtonContainer>
                  <PopupModal.Button
                    type={PopupModal.Button.type.NEGATIVE}
                    text={'TIDAK'}
                    onPress={() => this.setIssueModalVisible(false)}
                  />
                  <PopupModal.Button
                    text={'YA'}
                    onPress={() => this.issueBooking()}
                  />
                </PopupModal.ButtonContainer>
              </PopupModal>

              <PopupModal
                isVisible={isCancelModalVisible}
                onBackdropPress={() => this.setCancelModalVisible(false)}
              >
                <PopupModal.Header>Batalkan Pesanan</PopupModal.Header>
                <PopupModal.Content>
                  Klik YA untuk batalkan pesanan, klik TIDAK untuk kembali.
                </PopupModal.Content>
                <PopupModal.ButtonContainer>
                  <PopupModal.Button
                    type={PopupModal.Button.type.NEGATIVE}
                    text={'TIDAK'}
                    onPress={() => this.setCancelModalVisible(false)}
                  />
                  <PopupModal.Button
                    text={'YA'}
                    onPress={() => this.cancelBooking()}
                  />
                </PopupModal.ButtonContainer>
              </PopupModal>
            </View>
          </DRNModal>
        )}
      </React.Fragment>
    );
  }
}

export default BusTransactionList;
